/**
 * Which ATS vendors application autofill supports, and which of those are launched for every
 * signed-in user (TEC-715 staged rollout). Shared by the server routes and the client entry point
 * so both apply the same rule.
 *
 * SUPPORTED is kept in sync by hand with the recipe registry in
 * `lambda/crawler/utils/apply-autofill/ats-recipe-registry.ts` (duplicated rather than imported:
 * that registry pulls in Playwright code meant only for the crawler's Docker-image Lambda).
 */
export const SUPPORTED_AUTOFILL_VENDORS = [
  'teamtailor',
  'greenhouse',
  'ashby',
  'lever',
  'personio',
  'recruitee',
  'workable',
] as const;

export type AutofillVendor = (typeof SUPPORTED_AUTOFILL_VENDORS)[number];

/**
 * Vendors that passed the manual real-submission test pass (TEC-713) and are on for every
 * signed-in user. Empty until that pass has run: until then autofill only works with the
 * `dev_enable_apply_autofill` cookie. Add a vendor here only with a test-pass result on TEC-713.
 */
export const LAUNCHED_AUTOFILL_VENDORS: ReadonlySet<AutofillVendor> = new Set<AutofillVendor>([]);

export function isSupportedAutofillVendor(vendor: string | null | undefined): vendor is AutofillVendor {
  return Boolean(vendor) && (SUPPORTED_AUTOFILL_VENDORS as readonly string[]).includes(vendor as string);
}

/**
 * Autofill is available for a vendor when it is launched, or when the tester has the dev cookie
 * (which unlocks every supported vendor, including ones still being tested).
 */
export function isAutofillAvailableForVendor(vendor: string | null | undefined, hasDevCookie: boolean): boolean {
  if (!isSupportedAutofillVendor(vendor)) return false;
  return hasDevCookie || LAUNCHED_AUTOFILL_VENDORS.has(vendor);
}
